import * as R from 'ramda';
import {createAction} from 'redux-actions';
import URI from 'urijs';

import {fetchCampaignInviteIfNeeded} from 'js/creator/ducks/invites';

import history, {goToDashboard} from 'js/creator/history';

import * as DashboardApiClient from './dashboard/dashboard_api_client';
import * as OnboardingApiClient from './onboarding/onboarding_api_client';
import * as LocalStorageClient from './local_storage_client';

/**
 * DON'T MAKE ADDITIONS TO THIS.
 * Instead, create or extend the actiontype/actionCreator/reducer duckfiles
 * arranged by feature in js/creator/ducks
 * (and add any new reducers to js/creator/configure_store).
 * Be sure to follow the ducks pattern by export default-ing the reducer.
 */

const {UserType} = window.sn.enums;

export const Actions = {};
function registerAction(actionId, action) {
    Actions[actionId] = actionId;
    return createAction(actionId, action);
}

export const fetchUser = registerAction('FETCH_USER', DashboardApiClient.getUser);

const fetchRecentlyEndedCampaigns = registerAction(
    'FETCH_RECENTLY_ENDED_CAMPAIGNS',
    DashboardApiClient.getRecentlyEndedCampaigns
);

const submitInstagramPost = registerAction(
    'SUBMIT_INSTAGRAM_POST',
    DashboardApiClient.submitInstagramPost
);
const submitYoutubeVideo = registerAction(
    'SUBMIT_YOUTUBE_VIDEO',
    DashboardApiClient.submitYoutubeVideo
);

const removeServerAuthError = registerAction('REMOVE_SERVER_AUTH_ERROR');
const storeReturnUrl = registerAction('PERSIST_RETURN_URL', LocalStorageClient.persistReturnUrl);
const removeReturnUrl = registerAction('REMOVE_RETURN_URL', LocalStorageClient.removeReturnUrl);

export const submitContentApproval = registerAction(
    'SUBMIT_CONTENT_APPROVAL_IMAGE',
    async (inviteId, contentType, data) => ({
        inviteId,
        submission: await DashboardApiClient.submitForContentApproval(inviteId, contentType, data)
    })
);
export const pingContentApprovalPreviewUrl = registerAction(
    'PING_CONTENT_APPROVAL_PREVIEW_URL',
    async (inviteId, data) => ({
        inviteId,
        submission: await DashboardApiClient.pingContentApprovalPreviewUrl(inviteId, data)
    })
);
export const submitGenericContentForReview = registerAction(
    'SUBMIT_IMAGE_CONTENT_FOR_REVIEW',
    async (inviteId, contentMetadataId, data) => ({
        inviteId,
        submission: await DashboardApiClient.submitGenericContentForReview(
            inviteId,
            contentMetadataId,
            data
        )
    })
);
export const fetchUserExists = registerAction('USER_EXISTS', OnboardingApiClient.userExists);
export const persistCampaignInviteId = registerAction(
    'PERSIST_CAMPAIGN_INVITE_ID',
    LocalStorageClient.persistCampaignInviteId
);
export const fetchCampaignInviteId = registerAction(
    'FETCH_CAMPAIGN_INVITE_ID',
    LocalStorageClient.getCampaignInviteId
);
export const removeCampaignInviteId = registerAction(
    'REMOVE_CAMPAIGN_INVITE_ID',
    LocalStorageClient.removeCampaignInviteId
);
export const showNotification = registerAction('SHOW_NOTIFICATION');
export const fetchIsLoggedIn = registerAction('FETCH_IS_LOGGED_IN', DashboardApiClient.getLoggedIn);
export const updateProfile = registerAction('UPDATE_PROFILE', DashboardApiClient.updateProfile);

export const updateUpcomingCampaignState = registerAction(
    'UPDATE_UPCOMING_CAMPAIGN_STATE',
    R.identity
);

export const fetchUpcomingCampaigns = registerAction(
    'FETCH_UPCOMING_CAMPAIGNS',
    DashboardApiClient.getUpcomingCampaigns
);

export function doUpdateUpcomingCampaignState(nextState) {
    return dispatch => dispatch(updateUpcomingCampaignState(nextState));
}

export function fetchUpcomingCampaignsOnUpdate() {
    return dispatch => dispatch(fetchUpcomingCampaigns());
}

export const submitInviteRequest = registerAction(
    'SUBMIT_INVITE_REQUEST',
    async (campaignId, flightId) =>
        await DashboardApiClient.requestInviteToCampaignV2(campaignId, flightId)
);

export const fetchNumberOfCompletedCampaigns = registerAction(
    'FETCH_NUMBER_OF_COMPLETED_CAMPAIGNS',
    DashboardApiClient.getNumberOfCompletedCampaigns
);
export const fetchInstagramPosts = registerAction('FETCH_INSTAGRAM_POSTS', async inviteId => ({
    inviteId,
    instagramPosts: await DashboardApiClient.getInstagramPosts(inviteId)
}));

export const fetchSubmissions = registerAction('FETCH_SUBMISSIONS', async inviteId => ({
    inviteId,
    submissions: await DashboardApiClient.getSubmissions(inviteId)
}));

export function fetchRecentlyEndedCampaignsIfNeeded() {
    return (dispatch, getState) => {
        const state = getState();
        if (R.isNil(state.recentlyEndedCampaigns)) {
            return dispatch(fetchRecentlyEndedCampaigns());
        }

        return Promise.resolve();
    };
}

export function fetchUpcomingCampaignsIfNeeded() {
    return (dispatch, getState) => {
        const state = getState();

        if (R.isNil(state.upcomingCampaigns)) {
            return dispatch(fetchUpcomingCampaigns());
        }

        return Promise.resolve();
    };
}

export function fetchUserIfNeeded() {
    return (dispatch, getState) => {
        const state = getState();

        if (!state.user) {
            return dispatch(fetchUser()).then(action => {
                const {user, no_lead} = getState();
                if (no_lead) {
                    dispatch(storeReturnUrl(window.location.href));
                    // window.location.assign('/');
                } else if (!user || !user.lead_id || !user.type) {
                    dispatch(storeReturnUrl(window.location.href));
                    // window.location.assign('/');
                }

                if (user && !user.lead_id && user.type) {
                    switch (user.type) {
                        case UserType.INFLUENCER:
                            dispatch(
                                showNotification(
                                    'Your account is not verified with Instagram. Please login with Instagram to verify.'
                                )
                            );
                            break;
                        case UserType.BRAND:
                            dispatch(
                                showNotification(
                                    'You are logged in as a brand. Please sign out to use the publisher tools.'
                                )
                            );
                            break;
                        case UserType.ADMIN:
                            dispatch(
                                showNotification(
                                    'You are logged in as an administrator. Please sign out to use the publisher tools.'
                                )
                            );
                            break;
                        default:
                            dispatch(
                                showNotification(
                                    'You are logged in, but something is wrong. Please sign out to continue.'
                                )
                            );
                    }
                } else if (user && !user.type) {
                    dispatch(
                        showNotification(
                            'You are logged in, but something is wrong. Please sign out to continue.'
                        )
                    );
                }

                return action;
            });
        }
        if (state.returnUrl) {
            const returnPath = URI(state.returnUrl).pathname();
            history.pushState(null, returnPath);
            dispatch(removeReturnUrl());
        }

        return Promise.resolve();
    };
}

export function authError() {
    return (dispatch, getState) => {
        dispatch(storeReturnUrl(window.location.href));
        window.location.assign('/');
        const {serverAuthError} = getState();
        if (serverAuthError) {
            dispatch(removeServerAuthError());
            return dispatch(showNotification(serverAuthError));
        }
        return dispatch(showNotification('Please authorize with instagram to view this page.'));
    };
}

export function submitPostAndShowSuccess(inviteId, postId) {
    return async (dispatch, getState) =>
        dispatch(submitInstagramPost(inviteId, postId)).then(() => {
            history.pushState(null, `/creator/dashboard/campaigns/${inviteId}/submit_post_success`);
        });
}

export function submitVideoAndShowSuccess(inviteId, videoUrl) {
    return (dispatch, getState) =>
        dispatch(submitYoutubeVideo(inviteId, videoUrl)).then(() => {
            history.pushState(
                null,
                `/creator/dashboard/campaigns/${inviteId}/submit_youtube_video_success`
            );
        });
}

export function submitContentForApprovalPreview(inviteId, contentType, data) {
    return (dispatch, getState) =>
        dispatch(submitContentApproval(inviteId, contentType, data)).then(() => Promise.resolve());
}

export function pingExpectedContentForApprovalPreviewUrl(inviteId, data) {
    return (dispatch, getState) =>
        dispatch(pingContentApprovalPreviewUrl(inviteId, data)).then(action => {
            if (action.error) {
                history.pushState(null, '/creator/dashboard');
                return Promise.resolve();
            }

            return Promise.resolve();
        });
}

export function submitContentForApprovalReview(inviteId, contentMetadataId, data) {
    return (dispatch, getState) =>
        dispatch(submitGenericContentForReview(inviteId, contentMetadataId, data)).then(() => {
            history.pushState(
                null,
                `/creator/dashboard/campaigns/${inviteId}/submit_generic_content/${contentMetadataId}/success`
            );
        });
}

export function updateProfileAndShowNext(user) {
    return (dispatch, getState) =>
        dispatch(updateProfile(user)).then(() => {
            goToDashboard();
            return dispatch(
                showNotification({
                    message: 'Profile successfully updated.',
                    type: 'success'
                })
            );
        });
}

export function fetchUserWithInvite(inviteId) {
    return (dispatch, getState) =>
        Promise.all([
            dispatch(fetchCampaignInviteIfNeeded(inviteId)),
            dispatch(fetchUserIfNeeded())
        ]).then(() => {
            const {user, campaignInvite} = getState();
            const campaign = campaignInvite.campaign;
            const platform = campaign.platform;

            if (R.isNil(user)) {
                window.location.assign('/');
            }

            const didAuthorize = platform === 'instagram' || user.authedYoutube;

            if (user.lead_id !== campaignInvite.lead_id) {
                goToDashboard();
                return dispatch(showNotification('That invite was not sent to you.'));
            }

            if (!didAuthorize) {
                goToDashboard();
                return dispatch(
                    showNotification(`Please authorize with ${platform} to view this invite.`)
                );
            }
        });
}



// WEBPACK FOOTER //
// ./creator/actions.js